import { useState, type FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    service: '',
    budget: '',
    message: '',
    privacy: false,
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [step, setStep] = useState<'form' | 'confirm' | 'done'>('form');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const target = e.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
    const { name, type } = target;
    const value = type === 'checkbox' ? (target as HTMLInputElement).checked : target.value;

    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.company.trim()) newErrors.company = '会社名を入力してください';
    if (!formData.name.trim()) newErrors.name = 'お名前を入力してください';
    if (!formData.email.trim()) newErrors.email = 'メールアドレスを入力してください';
    if (!formData.service.trim()) newErrors.service = 'ご希望サービスを選択してください';
    if (!formData.message.trim()) newErrors.message = 'プロジェクト詳細を入力してください';
    if (formData.message.length > 500) newErrors.message = '500文字以内で入力してください';
    if (!formData.privacy) newErrors.privacy = 'プライバシーポリシーに同意してください';

    return newErrors;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // 入力 → 確認
    if (step === 'form') {
      setStep('confirm');
      return;
    }

    // 確認 → 送信
    if (step === 'confirm') {
      setIsSubmitting(true);
      setSubmitStatus('idle');

      try {
        const body = new URLSearchParams({
          company: formData.company,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          service: formData.service,
          budget: formData.budget,
          message: formData.message,
          privacy: formData.privacy ? '同意する' : '同意しない',
        }).toString();

        const response = await fetch('/send.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body,
        });

        if (response.ok) {
          setSubmitStatus('success');
          setStep('done');
          setFormData({
            company: '',
            name: '',
            email: '',
            phone: '',
            service: '',
            budget: '',
            message: '',
            privacy: false,
          });
        } else {
          setSubmitStatus('error');
        }
      } catch (error) {
        setSubmitStatus('error');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <section id="contact" className="py-32 bg-black relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(rgba(239, 68, 68, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(239, 68, 68, 0.3) 1px, transparent 1px)',
            backgroundSize: '80px 80px',
          }}
        ></div>
      </div>

      <div className="container mx-auto px-6 relative">
        <div className="max-w-5xl mx-auto">
          {/* ===== ヘッダー（元のまま） ===== */}
          <div className="text-center mb-16">
            <div className="inline-block mb-6">
              <span
                className="text-red-400 text-sm font-bold tracking-widest"
                style={{ fontFamily: '"Montserrat", sans-serif' }}
              >
                CONTACT US
              </span>
            </div>
            <h2
              className="text-6xl font-black text-white mb-8"
              style={{ fontFamily: '"Bebas Neue", sans-serif' }}
            >
              プロジェクト相談
            </h2>
            <div className="flex items-center justify-center gap-4">
              <div className="w-32 h-1 bg-gradient-to-r from-transparent via-red-500 to-transparent rounded-full"></div>
              <div className="w-4 h-4 bg-red-500 rounded-full"></div>
              <div className="w-32 h-1 bg-gradient-to-r from-transparent via-orange-500 to-transparent rounded-full"></div>
            </div>
            <p
              className="text-xl text-gray-300 mt-8"
              style={{ fontFamily: '"Montserrat", sans-serif' }}
            >
              お気軽にご相談ください。<strong className="text-red-400">無料コンサルティング</strong>
              も承ります。
            </p>
          </div>

          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-red-500/20 to-orange-500/20 rounded-3xl blur-xl"></div>

            <form
              id="contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="relative bg-white/5 backdrop-blur-xl rounded-3xl border border-red-500/30 p-10"
            >
              {/* ===== 完了画面 ===== */}
              {step === 'done' ? (
                <div className="space-y-6">
                  <div
                    className="bg-green-500/10 border border-green-500/30 text-green-400 px-6 py-4 rounded-lg text-center mt-2"
                    style={{ fontFamily: '"Montserrat", sans-serif' }}
                  >
                    お問い合わせを受け付けました。24時間以内にご連絡いたします。
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setStep('form');
                      setSubmitStatus('idle');
                    }}
                    className="w-full bg-gradient-to-r from-red-500 to-orange-500 text-white px-8 py-4 rounded-lg hover:shadow-2xl hover:shadow-red-500/50 transition-all font-bold whitespace-nowrap border border-red-400/30"
                    style={{ fontFamily: '"Montserrat", sans-serif' }}
                  >
                    もう一度入力する
                  </button>
                </div>
              ) : null}

              {/* ===== 入力フォーム ===== */}
              {step === 'form' && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label
                        htmlFor="company"
                        className="block text-sm font-bold text-red-400 mb-2"
                        style={{ fontFamily: '"Montserrat", sans-serif' }}
                      >
                        会社名 <span className="text-orange-400">*</span>
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        required
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white/5 border border-red-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-500/20 transition-all text-sm"
                        style={{ fontFamily: '"Montserrat", sans-serif' }}
                        placeholder="株式会社〇〇"
                      />
                      {errors.company && (
                        <p className="text-xs text-red-300 mt-1">{errors.company}</p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-bold text-red-400 mb-2"
                        style={{ fontFamily: '"Montserrat", sans-serif' }}
                      >
                        お名前 <span className="text-orange-400">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white/5 border border-red-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-500/20 transition-all text-sm"
                        style={{ fontFamily: '"Montserrat", sans-serif' }}
                        placeholder="山田 太郎"
                      />
                      {errors.name && (
                        <p className="text-xs text-red-300 mt-1">{errors.name}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-bold text-red-400 mb-2"
                        style={{ fontFamily: '"Montserrat", sans-serif' }}
                      >
                        メールアドレス <span className="text-orange-400">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white/5 border border-red-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-500/20 transition-all text-sm"
                        style={{ fontFamily: '"Montserrat", sans-serif' }}
                        placeholder="example@email.com"
                      />
                      {errors.email && (
                        <p className="text-xs text-red-300 mt-1">{errors.email}</p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="phone"
                        className="block text-sm font-bold text-red-400 mb-2"
                        style={{ fontFamily: '"Montserrat", sans-serif' }}
                      >
                        電話番号
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white/5 border border-red-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-500/20 transition-all text-sm"
                        style={{ fontFamily: '"Montserrat", sans-serif' }}
                        placeholder="03-0000-0000"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label
                        htmlFor="service"
                        className="block text-sm font-bold text-red-400 mb-2"
                        style={{ fontFamily: '"Montserrat", sans-serif' }}
                      >
                        ご希望サービス <span className="text-orange-400">*</span>
                      </label>
                      <select
                        id="service"
                        name="service"
                        required
                        value={formData.service}
                        onChange={handleChange}
                        className="w-full px-4 py-3 pr-8 bg-white/5 border border-red-500/30 rounded-lg text-white focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-500/20 transition-all text-sm"
                        style={{ fontFamily: '"Montserrat", sans-serif' }}
                      >
                        <option value="">選択してください</option>
                        <option value="ブランディング">ブランディング</option>
                        <option value="広告制作">広告制作</option>
                        <option value="デジタルマーケティング">デジタルマーケティング</option>
                        <option value="映像制作">映像制作</option>
                        <option value="Webサイト制作">Webサイト制作</option>
                        <option value="イベント企画">イベント企画</option>
                        <option value="その他">その他</option>
                      </select>
                      {errors.service && (
                        <p className="text-xs text-red-300 mt-1">{errors.service}</p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="budget"
                        className="block text-sm font-bold text-red-400 mb-2"
                        style={{ fontFamily: '"Montserrat", sans-serif' }}
                      >
                        ご予算
                      </label>
                      <select
                        id="budget"
                        name="budget"
                        value={formData.budget}
                        onChange={handleChange}
                        className="w-full px-4 py-3 pr-8 bg-white/5 border border-red-500/30 rounded-lg text-white focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-500/20 transition-all text-sm"
                        style={{ fontFamily: '"Montserrat", sans-serif' }}
                      >
                        <option value="">選択してください</option>
                        <option value="〜100万円">〜100万円</option>
                        <option value="100〜300万円">100〜300万円</option>
                        <option value="300〜500万円">300〜500万円</option>
                        <option value="500〜1000万円">500〜1000万円</option>
                        <option value="1000万円以上">1000万円以上</option>
                        <option value="要相談">要相談</option>
                      </select>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label
                      htmlFor="message"
                      className="block text-sm font-bold text-red-400 mb-2"
                      style={{ fontFamily: '"Montserrat", sans-serif' }}
                    >
                      プロジェクト詳細 <span className="text-orange-400">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={6}
                      maxLength={500}
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white/5 border border-red-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-500/20 transition-all resize-none text-sm"
                      style={{ fontFamily: '"Montserrat", sans-serif' }}
                      placeholder="プロジェクトの詳細、ご要望、納期などをお聞かせください（500文字以内）"
                    ></textarea>
                    <div className="flex justify-between items-center mt-2">
                      {errors.message && (
                        <p className="text-xs text-red-300">{errors.message}</p>
                      )}
                      <p
                        className="text-xs text-gray-500 ml-auto"
                        style={{ fontFamily: '"Montserrat", sans-serif' }}
                      >
                        {formData.message.length}/500文字
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 mb-8">
                    <input
                      type="checkbox"
                      id="privacy"
                      name="privacy"
                      required
                      checked={formData.privacy}
                      onChange={handleChange}
                      className="mt-1 w-5 h-5 rounded border-red-500/30 bg-white/5 text-red-500 focus:ring-2 focus:ring-red-500/20 cursor-pointer"
                    />
                    <label
                      htmlFor="privacy"
                      className="text-sm text-gray-400 cursor-pointer"
                      style={{ fontFamily: '"Montserrat", sans-serif' }}
                    >
                      <a
                        href="/privacy"
                        className="text-red-400 hover:text-red-300 underline"
                      >
                        プライバシーポリシー
                      </a>
                      に同意します <span className="text-orange-400">*</span>
                    </label>
                  </div>
                  {errors.privacy && (
                    <p className="text-xs text-red-300 -mt-6 mb-6">{errors.privacy}</p>
                  )}

                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-red-500 to-orange-500 text-white px-8 py-4 rounded-lg hover:shadow-2xl hover:shadow-red-500/50 transition-all font-bold whitespace-nowrap border border-red-400/30"
                    style={{ fontFamily: '"Montserrat", sans-serif' }}
                  >
                    確認画面へ進む
                  </button>

                  {submitStatus === 'error' && (
                    <div
                      className="bg-red-500/10 border border-red-500/30 text-red-400 px-6 py-4 rounded-lg text-center mt-6"
                      style={{ fontFamily: '"Montserrat", sans-serif' }}
                    >
                      送信に失敗しました。もう一度お試しください。
                    </div>
                  )}
                </>
              )}

              {/* ===== 確認画面 ===== */}
              {step === 'confirm' && (
                <div className="space-y-6">
                  <p
                    className="text-sm text-gray-300"
                    style={{ fontFamily: '"Montserrat", sans-serif' }}
                  >
                    以下の内容で送信します。よろしければ「送信する」を押してください。
                  </p>

                  <div
                    className="space-y-4 text-sm text-gray-100"
                    style={{ fontFamily: '"Montserrat", sans-serif' }}
                  >
                    <div>
                      <div className="font-bold text-red-300">会社名</div>
                      <p className="mt-1 break-words">
                        {formData.company || '（未入力）'}
                      </p>
                    </div>
                    <div>
                      <div className="font-bold text-red-300">お名前</div>
                      <p className="mt-1 break-words">
                        {formData.name || '（未入力）'}
                      </p>
                    </div>
                    <div>
                      <div className="font-bold text-red-300">メールアドレス</div>
                      <p className="mt-1 break-words">
                        {formData.email || '（未入力）'}
                      </p>
                    </div>
                    <div>
                      <div className="font-bold text-red-300">電話番号</div>
                      <p className="mt-1 break-words">
                        {formData.phone || '（未入力）'}
                      </p>
                    </div>
                    <div>
                      <div className="font-bold text-red-300">ご希望サービス</div>
                      <p className="mt-1 break-words">
                        {formData.service || '（未入力）'}
                      </p>
                    </div>
                    <div>
                      <div className="font-bold text-red-300">ご予算</div>
                      <p className="mt-1 break-words">
                        {formData.budget || '（未入力）'}
                      </p>
                    </div>
                    <div>
                      <div className="font-bold text-red-300">プロジェクト詳細</div>
                      <p className="mt-1 whitespace-pre-wrap break-words">
                        {formData.message || '（未入力）'}
                      </p>
                    </div>
                    <div>
                      <div className="font-bold text-red-300">
                        プライバシーポリシー同意
                      </div>
                      <p className="mt-1">
                        {formData.privacy ? '同意する' : '同意しない'}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4 mt-8">
                    <button
                      type="button"
                      onClick={() => setStep('form')}
                      className="w-full sm:w-1/2 border border-red-500/40 text-red-200 py-3 rounded-lg hover:bg-white/5 transition-all font-bold"
                      style={{ fontFamily: '"Montserrat", sans-serif' }}
                    >
                      修正する
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full sm:w-1/2 bg-gradient-to-r from-red-500 to-orange-500 text-white px-8 py-3 rounded-lg hover:shadow-2xl hover:shadow-red-500/50 transition-all font-bold disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap border border-red-400/30"
                      style={{ fontFamily: '"Montserrat", sans-serif' }}
                    >
                      {isSubmitting ? '送信中...' : '送信する'}
                    </button>
                  </div>

                  {submitStatus === 'error' && (
                    <div
                      className="bg-red-500/10 border border-red-500/30 text-red-400 px-6 py-4 rounded-lg text-center mt-4"
                      style={{ fontFamily: '"Montserrat", sans-serif' }}
                    >
                      送信に失敗しました。もう一度お試しください。
                    </div>
                  )}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
